import { createContext, PropsWithChildren, useEffect, useState } from 'react';
import { isMobileDevice, useApi } from '@repo-level/core-front';

import { mobileApp, MobileAppApi } from 'api/cms/mobileApp';
import { isIos } from 'helpers/common';

interface MobileAppContextData {
  data?: MobileAppApi;
  shouldShowIosMobile: boolean;
  shouldShowIosDesktop: boolean;
  shouldShowIos: boolean;
  shouldShowAndroidMobile: boolean;
  shouldShowAndroidDesktop: boolean;
  shouldShowAndroid: boolean;
  error: boolean;
  pending: boolean;
}

const initialState = {
  shouldShowIosMobile: true,
  shouldShowIosDesktop: true,
  shouldShowIos: true,
  shouldShowAndroidMobile: true,
  shouldShowAndroidDesktop: true,
  shouldShowAndroid: true,
};

export const MobileAppContext = createContext<MobileAppContextData>(
  null as any
);

export const MobileAppContextProvider = ({
  children,
}: PropsWithChildren<{}>) => {
  const [requestMobileApp, { error, pending, data }] = useApi(mobileApp);

  const [state, setState] = useState(initialState);

  useEffect(() => {
    requestMobileApp();
  }, [requestMobileApp]);

  useEffect(() => {
    if (error) {
      setState((prevState) =>
        Object.keys(prevState).reduce((acc, key) => {
          acc[key as keyof typeof prevState] = false;
          return acc;
        }, {} as typeof initialState)
      );
    }

    if (data) {
      const { showIos, iosLink, showAndroid, androidApk } = data;

      const { ios, android } = {
        ios: {
          enabled: showIos && !!iosLink,
          isMobile: isMobileDevice && isIos(),
          isDesktop: !isMobileDevice,
        },
        android: {
          enabled: showAndroid && !!androidApk,
          isMobile: isMobileDevice && !isIos(),
          isDesktop: !isMobileDevice,
        },
      };

      setState({
        shouldShowIosMobile: ios.enabled && ios.isMobile,
        shouldShowIosDesktop: ios.enabled && ios.isDesktop,
        shouldShowIos: ios.enabled && (ios.isMobile || ios.isDesktop),
        shouldShowAndroidMobile: android.enabled && android.isMobile,
        shouldShowAndroidDesktop: android.enabled && android.isDesktop,
        shouldShowAndroid:
          android.enabled && (android.isMobile || android.isDesktop),
      });
    }
  }, [data, error]);

  return (
    <MobileAppContext.Provider value={{ ...state, data, error, pending }}>
      {children}
    </MobileAppContext.Provider>
  );
};
